import React, { useState, useEffect } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Plus, Trash2, MapPin, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INPUT_CLS = "rounded-none border-[#E2E8F0] focus-visible:ring-0 focus-visible:border-[#1B2A6B] h-11 text-sm";

const FormField = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="text-[11px] font-bold tracking-widest uppercase text-[#1B2A6B] block">{label}</label>
    {children}
  </div>
);

export default function CitiesPage() {
  const [cities, setCities]   = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState({ city_name: "", description: "", region_id: "" });

  const fetchCities = async () => {
    try {
      const [citiesResponse, regionsResponse] = await Promise.all([
        axios.get(`${API}/cities`, { withCredentials: true }),
        axios.get(`${API}/regions`, { withCredentials: true }),
      ]);
      setCities(citiesResponse.data);
      setRegions(regionsResponse.data);
    } catch {
      toast.error("Failed to load cities.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCities(); }, []);

  const resetForm = () => {
    setEditing(null);
    setForm({ city_name: "", description: "", region_id: "" });
  };

  const openAdd = () => {
    if (regions.length === 0) {
      toast.error("Add a region before adding cities.");
      return;
    }
    resetForm();
    setShowAdd(true);
  };

  const openEdit = (city) => {
    setEditing(city);
    setForm({ city_name: city.city_name, description: city.description || "", region_id: city.region_id || "" });
    setShowAdd(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.city_name.trim()) { toast.error("City name is required."); return; }
    if (!form.region_id) { toast.error("Please select a region."); return; }
    setSaving(true);
    try {
      const request = editing
        ? axios.patch(`${API}/cities/${editing.id}`, form, { withCredentials: true })
        : axios.post(`${API}/cities`, form, { withCredentials: true });
      const { data } = await request;
      setCities(prev => {
        const next = editing ? prev.map(city => city.id === data.id ? data : city) : [...prev, data];
        return next.sort((a, b) => a.city_name.localeCompare(b.city_name));
      });
      toast.success(editing ? "City updated." : "City added successfully!");
      setShowAdd(false);
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add city.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this city?")) return;
    try {
      await axios.delete(`${API}/cities/${id}`, { withCredentials: true });
      setCities(prev => prev.filter(c => c.id !== id));
      toast.success("City deleted.");
    } catch {
      toast.error("Failed to delete city.");
    }
  };

  return (
    <div className="space-y-5" data-testid="cities-page">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#1B2A6B]">Cities</h2>
          <p className="text-xs text-[#475569] mt-0.5">{cities.length} cit{cities.length !== 1 ? "ies" : "y"} in master data</p>
        </div>
        <Button
          data-testid="add-city-btn"
          onClick={openAdd}
          className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-10 px-5 border-b-2 border-[#FFD700]"
        >
          <Plus size={14} className="mr-1.5" />Add City
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E2E8F0]">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-sm text-[#94A3B8]">Loading...</div>
        ) : cities.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <MapPin size={40} className="text-[#E2E8F0]" />
            <p className="text-sm text-[#94A3B8]">No cities yet. <button onClick={openAdd} className="text-[#1B2A6B] font-semibold underline">Add your first city</button></p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cities-table">
              <thead>
                <tr className="bg-[#F9FAFB] border-b border-[#E2E8F0]">
                  {["#", "City Name", "Region", "Description", "Added On", ""].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[#475569] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cities.map((city, idx) => (
                  <tr key={city.id} data-testid={`city-row-${idx}`} className={`border-b border-[#E2E8F0] ${idx % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}`}>
                    <td className="px-4 py-3 text-[#94A3B8] text-xs font-mono">{idx + 1}</td>
                    <td className="px-4 py-3 font-semibold text-[#0F172A]">{city.city_name}</td>
                    <td className="px-4 py-3">
                      {city.region_name ? (
                        <span className="inline-flex items-center gap-2 text-xs font-semibold text-[#475569]">
                          <span className="w-3 h-3 rounded-full border border-black/10" style={{ backgroundColor: city.region_color_code }} />
                          {city.region_name}
                        </span>
                      ) : <span className="text-xs font-semibold text-orange-600">Unassigned</span>}
                    </td>
                    <td className="px-4 py-3 text-[#475569]">{city.description || "—"}</td>
                    <td className="px-4 py-3 text-xs text-[#475569] whitespace-nowrap">{format(new Date(city.created_at), "dd MMM yyyy")}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button data-testid={`edit-city-${idx}`} onClick={() => openEdit(city)} className="text-[#94A3B8] hover:text-[#1B2A6B] transition-colors p-1" title="Edit"><Pencil size={14} /></button>
                        <button data-testid={`delete-city-${idx}`} onClick={() => handleDelete(city.id)} className="text-[#CBD5E1] hover:text-red-500 transition-colors p-1" title="Delete"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add / Edit City Dialog */}
      <Dialog open={showAdd} onOpenChange={o => { if (!o) resetForm(); setShowAdd(o); }}>
        <DialogContent className="max-w-md rounded-none p-0 gap-0">
          <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700]">
            <DialogTitle className="text-white font-bold tracking-wide text-base">{editing ? "Edit City" : "Add New City"}</DialogTitle>
            <p className="text-white/60 text-xs mt-0.5">Link every city to its operating region</p>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-5 space-y-4">
              <FormField label="City Name *">
                <Input
                  value={form.city_name}
                  onChange={e => setForm(f => ({ ...f, city_name: e.target.value }))}
                  placeholder="e.g. Mumbai"
                  data-testid="city-name-input"
                  className={INPUT_CLS}
                  autoFocus
                />
              </FormField>
              <FormField label="Region *">
                <select
                  value={form.region_id}
                  onChange={e => setForm(f => ({ ...f, region_id: e.target.value }))}
                  data-testid="city-region-select"
                  className="w-full rounded-none border border-[#E2E8F0] focus:border-[#1B2A6B] focus:outline-none h-11 px-3 text-sm bg-white text-[#0F172A]"
                >
                  <option value="">Select a region</option>
                  {regions.map(region => <option key={region.id} value={region.id}>{region.name}</option>)}
                </select>
              </FormField>
              <FormField label="Description">
                <textarea
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Optional notes about this city..."
                  rows={3}
                  data-testid="city-description-input"
                  className="w-full rounded-none border border-[#E2E8F0] focus:border-[#1B2A6B] focus:outline-none px-3 py-2.5 text-sm text-[#0F172A] placeholder:text-[#94A3B8] resize-none"
                />
              </FormField>
            </div>
            <DialogFooter className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E2E8F0] gap-2">
              <button type="button" onClick={() => { resetForm(); setShowAdd(false); }} data-testid="city-cancel-btn" className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] border border-[#E2E8F0] hover:border-[#CBD5E1] transition-colors">Cancel</button>
              <Button type="submit" data-testid="city-save-btn" disabled={saving} className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-9 px-6 border-b-2 border-[#FFD700] disabled:opacity-60">
                {saving ? "Saving..." : editing ? "Update City" : "Save City"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
